import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  applyFilter,
  deleteFilter as deleteFilterApi,
  fetchFilters,
} from '../api';
import Header from '../components/Header';

interface Filter {
  id: number;
  name: string;
  created_at: string;
}

interface FilterResult {
  headline_id: number;
  title: string;
  url: string;
  source: string;
  score: number;
}

export default function FilterApplyPage() {
  const [filters, setFilters] = useState<Filter[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [results, setResults] = useState<FilterResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchFilters()
      .then((data) => setFilters(data.filters))
      .catch(() => setError('Failed to load filters'));
  }, []);

  const handleApply = async (filterId: number) => {
    setSelectedId(filterId);
    setLoading(true);
    setError(null);
    setResults(null);
    try {
      const data = await applyFilter(filterId);
      setResults(data.results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply filter');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (filterId: number) => {
    try {
      await deleteFilterApi(filterId);
      setFilters((prev) => prev.filter((f) => f.id !== filterId));
      if (selectedId === filterId) {
        setSelectedId(null);
        setResults(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete filter');
    }
  };

  return (
    <div className="page-filter-apply">
      <Header />
      <Link className="back-link" to="/">
        &larr; Back to headlines
      </Link>

      <h2>Filters</h2>
      <Link to="/filters/new" className="create-filter-link">
        + Create new filter
      </Link>

      {filters.length === 0 && <p className="empty">No filters yet.</p>}

      {filters.length > 0 && (
        <div className="filter-list">
          {filters.map((f) => (
            <div
              key={f.id}
              className={`filter-item ${selectedId === f.id ? 'active' : ''}`}
            >
              <button
                type="button"
                className="filter-select-btn"
                onClick={() => handleApply(f.id)}
              >
                {f.name}
              </button>
              <button
                type="button"
                className="filter-delete-btn"
                onClick={() => handleDelete(f.id)}
              >
                delete
              </button>
            </div>
          ))}
        </div>
      )}

      {error && <p className="error">{error}</p>}
      {loading && <p className="summary-loading">Applying filter...</p>}

      {results !== null && !loading && (
        <div className="search-results">
          <p className="result-count">{results.length} results</p>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Score</th>
                <th>Headline</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, i) => (
                <tr key={r.headline_id}>
                  <td className="col-rank">{i + 1}</td>
                  <td className="col-dist">{r.score.toFixed(4)}</td>
                  <td>
                    <a href={r.url} target="_blank" rel="noreferrer">
                      {r.title}
                    </a>
                  </td>
                  <td className="col-source">{r.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
